import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
    Backdrop,
    Button,
    makeStyles,
} from '@material-ui/core';
import HtmlContent from '../../HtmlContent';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
    },
    box: {
        width: 160,
        height: 80,
        background: theme.youwell.colors.secondaryBackground,

        [theme.breakpoints.down('xs')]: {
            width: 115,
            height: 60,
        },
    },
    inActive: {
        cursor: 'default',
        pointerEvents: 'none',
    },
    empty: {
        visibility: 'hidden',
    },
    boxRow: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    first: {
        marginBottom: theme.spacing(8),
        [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(4),
        },
    },
    last: {
        marginTop: theme.spacing(8),
        [theme.breakpoints.down('xs')]: {
            marginTop: theme.spacing(4),
        },
    },
    detailsBackdrop: {
        zIndex: 1,
    },
    detailsBox: {
        zIndex: 2,
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(4),
        left: theme.spacing(4),
        minHeight: `calc(100% - ${2 * theme.spacing(4)}px)`,
        border: theme.youwell.borders.primary,
        borderRadius: theme.youwell.shapes.borderRadius,
        background: theme.palette.background.paper,
        padding: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        [theme.breakpoints.down('xs')]: {
            top: theme.spacing(2),
            right: theme.spacing(2),
            left: theme.spacing(2),
            minHeight: `calc(100% - ${2 * theme.spacing(2)}px)`,
            padding: theme.spacing(2),
        },
    },
    lineWrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: '100vw',
        pointerEvents: 'none',
        color: theme.palette.primary.main,
    },
}), { name: 'ConnectedBoxes' });

const arrowDirections = {
    BottomToTop: 1,
    LeftToRight: 2,
    RightToLeft: 3,
    TopToBottom: 4,
    LeftToTop: 5,
    RightToTop: 6,
    BottomToRight: 7,
    BottomToLeft: 8,
    TopToRight: 9,
    Self: 10,
};

const getDirection = (from, to) => {
    if (from === to) return arrowDirections.Self;

    switch (from) {
    case 0: {
        switch (to) {
        case 1: case 2:
            return arrowDirections.RightToLeft;
        default:
            return arrowDirections.BottomToTop;
        }
    }
    case 1: {
        switch (to) {
        case 2:
            return arrowDirections.RightToLeft;
        case 3:
            return arrowDirections.LeftToTop;
        case 4:
            return arrowDirections.BottomToTop;
        case 5:
            return arrowDirections.RightToTop;
        default:
            return arrowDirections.BottomToTop;
        }
    }
    case 3: {
        switch (to) {
        case 4: case 5:
            return arrowDirections.RightToLeft;
        case 6:
            return arrowDirections.BottomToTop;
        default:
            return arrowDirections.BottomToLeft;
        }
    }
    case 4: {
        switch (to) {
        case 5:
            return arrowDirections.RightToLeft;
        case 6:
            return arrowDirections.LeftToTop;
        case 7:
            return arrowDirections.BottomToTop;
        default:
            return arrowDirections.RightToTop;
        }
    }
    case 5: {
        switch (to) {
        case 6:
            return arrowDirections.LeftToTop;
        case 7:
            return arrowDirections.BottomToRight;
        default:
            return arrowDirections.BottomToTop;
        }
    }
    default:
        return arrowDirections.BottomToTop;
    }
}

const calculate = (from, to, direction) => {
    const fromEl = document.getElementById(from);
    const toEl = document.getElementById(to);

    if (!fromEl || !toEl) {
        return '';
    }

    const fromPosRect = fromEl.getBoundingClientRect();
    const toPosRect = toEl.getBoundingClientRect();

    const spacing = 8;
    let fromPos;
    let toPos;
    switch (direction) {
    case arrowDirections.BottomToTop:
        fromPos = { x: fromPosRect.left + fromEl.offsetWidth / 2, y: fromPosRect.bottom + spacing };
        toPos = { x: toPosRect.left + fromEl.offsetWidth / 2, y: toPosRect.top - spacing * 1.5 };
        break;
    case arrowDirections.TopToBottom:
        fromPos = { x: fromPosRect.left + fromEl.offsetWidth / 2, y: fromPosRect.top + spacing };
        toPos = { x: toPosRect.left + fromEl.offsetWidth / 2, y: toPosRect.bottom + spacing };
        break;
    case arrowDirections.LeftToRight:
        fromPos = { x: fromPosRect.left - spacing, y: fromPosRect.top + fromEl.offsetHeight / 2 };
        toPos = { x: toPosRect.right + spacing * 1.5, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    case arrowDirections.RightToLeft:
        fromPos = { x: fromPosRect.right + spacing, y: fromPosRect.top + fromEl.offsetHeight / 2 };
        toPos = { x: toPosRect.left - spacing, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    case arrowDirections.LeftToTop:
        fromPos = { x: fromPosRect.left - spacing, y: fromPosRect.top + fromEl.offsetHeight / 2 };
        toPos = { x: toPosRect.left + fromEl.offsetWidth / 2, y: toPosRect.top - spacing };
        break;
    case arrowDirections.RightToTop:
        fromPos = { x: fromPosRect.right + spacing, y: fromPosRect.top + fromEl.offsetHeight / 2 };
        toPos = { x: toPosRect.left + fromEl.offsetWidth / 2, y: toPosRect.top - spacing };
        break;
    case arrowDirections.BottomToRight:
        fromPos = { x: fromPosRect.left + fromEl.offsetWidth / 2, y: fromPosRect.bottom + spacing };
        toPos = { x: toPosRect.right + spacing * 1.5, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    case arrowDirections.BottomToLeft:
        fromPos = { x: fromPosRect.left + fromEl.offsetWidth / 2, y: fromPosRect.bottom + spacing };
        toPos = { x: toPosRect.left - spacing, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    case arrowDirections.TopToRight:
        fromPos = { x: fromPosRect.left + fromEl.offsetWidth / 2, y: fromPosRect.top + spacing };
        toPos = { x: toPosRect.right + spacing * 1.5, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    case arrowDirections.Self:
        fromPos = { x: fromPosRect.left + fromEl.offsetWidth / 2, y: fromPosRect.top + spacing };
        toPos = { x: toPosRect.right + spacing * 1.5, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    default:
        fromPos = { x: fromPosRect.left - spacing, y: fromPosRect.top + fromEl.offsetHeight / 2 };
        toPos = { x: toPosRect.right + spacing, y: toPosRect.top + toEl.offsetHeight / 2 };
        break;
    }

    return `M${fromPos.x},${fromPos.y} Q${fromPos.x},${fromPos.y} ${toPos.x},${toPos.y}`;
    // return `M${fromPos.x},${fromPos.y} C${fromPos.x - 80},${fromPos.y} ${toPos.x + 80},${toPos.y} ${toPos.x},${toPos.y}`;
};

const ConnectingLine = ({ line }) => {
    const classes = useStyles();
    const lineEl = React.useRef(null);
    const { from, to, direction, type } = line;

    const update = React.useCallback(() => {
        if (lineEl.current) {
            lineEl.current.setAttribute('d', calculate(from, to, direction));
        }
    }, [from, to, direction]);

    React.useEffect(() => {
        update();
    }, [update]);

    React.useEffect(() => {
        window.addEventListener('scroll', update);
        window.addEventListener('resize', update);

        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [update]);


    const showLeftArrow = !type || type === 'left' || type === 'both';
    const showRightArrow = !type || type === 'right' || type === 'both';

    return (
        <div className={classes.lineWrapper}>
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
                <g fill="none" stroke="currentColor" strokeWidth="2" markerEnd={showRightArrow ? 'url(#arrowheadend)' : null} markerStart={showLeftArrow ? 'url(#arrowheadstart)' : null}>
                    <path id="arrowLeft" ref={lineEl} d={calculate(from, to, direction)} />
                </g>
            </svg>
        </div>
    );
};

ConnectingLine.propTypes = {
    line: PropTypes.object.isRequired,
};

const ConnectedBoxes = ({
    task, element, className,
}) => {
    const classes = useStyles();
    const [showDetailsBoxIx, setShowDetailsBoxIx] = React.useState(null)

    if (!element.settings) {
        return null;
    }

    const boxes = [];
    const connectingLines = [];
    Object.entries(element.settings).forEach(([key, value], i) => {
        if (key.startsWith('box-nr-') && value) { // box-nr-1 -> Box index = 0
            const boxNr = Number(key.substring(7)) - 1;
            const box = task.contentElements?.find(e => e.id === Number(value));
            if (!Number.isNaN(boxNr) && box) {
                boxes[boxNr] = {
                    ...box,
                    tagId: `connectedBox_${element.id}_box${box.id}`,
                };
            }
        }
        if (key.startsWith('line-')) { // line-X-Y -> From X to Y
            const from = Number(key.substring(5, 6)) - 1;
            const to = Number(key.substring(7, 8)) - 1;
            if (!Number.isNaN(from) && !Number.isNaN(to)) {
                connectingLines.push({
                    id: i,
                    from: boxes[from]?.tagId,
                    to: boxes[to]?.tagId,
                    direction: getDirection(from, to),
                    type: value, // Direction for arrow or type: left, right, both, none, .. dotted?
                });
            }
        }
    });

    const renderBox = (index) => {
        const box = boxes[index];
        const hasContent = box?.htmlContent;

        if (box) {
            return (
                <Button color="primary" variant="outlined" onClick={() => setShowDetailsBoxIx(index)} className={classnames(classes.box, { [classes.inActive]: !hasContent })} id={box.tagId}>
                    {box.title}
                </Button>
            );
        }
        return <div className={classnames(classes.box, classes.empty)} />
    }

    return (
        <div className={classnames(className, classes.root)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="0" height="0">
                <defs>
                    <marker
                        id="arrowheadend"
                        viewBox="0 0 10 10"
                        refX="3"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto"
                        fill="currentColor"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" />
                    </marker>
                    <marker
                        id="arrowheadstart"
                        viewBox="0 0 10 10"
                        refX="3"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto"
                        fill="currentColor"
                    >
                        <path d="M 10 0 L 0 5 L 10 10 z" />
                    </marker>
                </defs>
            </svg>
            <div className={classnames(classes.boxRow, classes.first)}>
                {renderBox(0)}
                {renderBox(1)}
                {renderBox(2)}
            </div>
            <div className={classnames(classes.boxRow)}>
                {renderBox(3)}
                {renderBox(4)}
                {renderBox(5)}
            </div>
            <div className={classnames(classes.boxRow, classes.last)}>
                {renderBox(6)}
                {renderBox(7)}
                {renderBox(8)}
            </div>

            {connectingLines.map(line => (
                <ConnectingLine key={line.id} line={line} classes={classes} />
            ))}

            {boxes[showDetailsBoxIx] && (
                <>
                    <Backdrop open invisible onClick={() => setShowDetailsBoxIx(null)} className={classes.detailsBackdrop} />
                    <div className={classes.detailsBox}>
                        <HtmlContent html={boxes[showDetailsBoxIx].htmlContent} className={classes.htmlContent} />
                    </div>
                </>
            )}
        </div>
    );
}

ConnectedBoxes.propTypes = {
    element: PropTypes.object.isRequired,
    task: PropTypes.object.isRequired,
    className: PropTypes.string,
};

ConnectedBoxes.defaultProps = {
    className: null,
};

export default ConnectedBoxes;
